import React, { useState } from "react";
import { X, MessageCircle, Copy, QrCode, UserPlus, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { buildReferralLink } from "@/lib/referral";

export function InviteButton({ className = "", "data-testid": testId = "invite-partner-button" }) {
  const [open, setOpen] = useState(false);
  const { partner_label } = useSettings();
  const labelP = partner_label || "Agent";
  return (
    <>
      <Button data-testid={testId} onClick={() => setOpen(true)}
        className={`rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold ${className}`}>
        <UserPlus className="w-4 h-4 mr-2" /> Invite {labelP}
      </Button>
      {open && <InviteModal onClose={() => setOpen(false)} />}
    </>
  );
}

function InviteModal({ onClose }) {
  const { user } = useAuth();
  const { partner_label } = useSettings();
  const labelP = partner_label || "Agent";
  const ref = user?.referral_code || "";
  const waLink = buildReferralLink(ref, "whatsapp", "invite");
  const copyL = buildReferralLink(ref, "copy", "invite");
  const smsLink = buildReferralLink(ref, "sms", "invite");
  const qrTarget = encodeURIComponent(buildReferralLink(ref, "qr", "invite"));
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${qrTarget}`;

  const inviteText = `Hi! Join Crece as my ${labelP.toLowerCase()} and earn weekly from financial products. No investment needed. Sign up here: ${waLink}\n\nUse my referral code: ${ref}`;

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(inviteText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(`${inviteText}`);
    toast.success("Invite copied — paste anywhere!");
  };

  const smsInvite = () => {
    const body = encodeURIComponent(`Join Crece as my ${labelP.toLowerCase()} — sign up: ${smsLink} | Ref: ${ref}`);
    window.location.href = `sms:?body=${body}`;
  };

  const emailInvite = () => {
    const subject = encodeURIComponent("Join me on Crece — earn from financial products");
    const body = encodeURIComponent(inviteText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-xl w-full p-7 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Grow your team</div>
            <h3 className="font-display text-2xl font-black mt-1">Invite an {labelP}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="bg-emerald-900 text-white rounded-xl p-5 mb-5">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-400">Your Referral Code</div>
          <div className="font-display text-3xl font-black mt-1 tracking-wider" data-testid="invite-ref-code">{ref}</div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Button data-testid="invite-whatsapp" onClick={shareWhatsApp}
            className="h-auto py-4 flex-col gap-1.5 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-white">
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs font-bold">WhatsApp</span>
          </Button>
          <Button data-testid="invite-copy" onClick={copyInvite} variant="outline"
            className="h-auto py-4 flex-col gap-1.5 rounded-xl border-stone-300 text-stone-900 hover:bg-stone-50">
            <Copy className="w-5 h-5" />
            <span className="text-xs font-bold">Copy</span>
          </Button>
          <Button data-testid="invite-sms" onClick={smsInvite} variant="outline"
            className="h-auto py-4 flex-col gap-1.5 rounded-xl border-stone-300 text-stone-900 hover:bg-stone-50">
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs font-bold">SMS</span>
          </Button>
          <Button data-testid="invite-email" onClick={emailInvite} variant="outline"
            className="h-auto py-4 flex-col gap-1.5 rounded-xl border-stone-300 text-stone-900 hover:bg-stone-50">
            <Mail className="w-5 h-5" />
            <span className="text-xs font-bold">Email</span>
          </Button>
        </div>

        <div className="mt-5 pt-5 border-t border-stone-200" data-testid="invite-qr-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <QrCode className="w-4 h-4 text-stone-600" />
              <span className="text-xs font-bold uppercase tracking-wider text-stone-600">Scan to sign up (in person)</span>
            </div>
            <a href="/flyer" target="_blank" rel="noopener noreferrer" data-testid="open-flyer-link"
              className="text-xs font-bold text-emerald-900 hover:text-emerald-950 underline">
              Open printable flyer →
            </a>
          </div>
          <div className="flex items-center gap-4">
            <img src={qrUrl} alt="Referral QR code" className="w-32 h-32 border border-stone-200 rounded-lg bg-white p-1" />
            <div className="flex-1">
              <p className="text-sm text-stone-600">Show this QR at meetings, events, or print it on flyers — anyone scanning lands on Crece signup with your code pre-filled.</p>
              <a href={qrUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-xs font-bold text-emerald-900 hover:text-emerald-950">Open full-size QR →</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
